export const RialticRoles = {
  content: 'Content@rialtic',
  design: 'Design@rialtic',
  engCore: 'Engineering-Core-Claims@rialtic',
  engCloud: 'Engineering-Cloud@rialtic',
  engEngines: 'Engineering-Engines@rialtic',
  engWorkflows: 'Engineering-Workflows@rialtic',
  cs: 'Client-Success@rialtic',
  gtm: 'GTM@rialtic',
  product: 'Product@rialtic',
}

export const RialticEngineering = [
  RialticRoles.engCore,
  RialticRoles.engCloud,
  RialticRoles.engEngines,
  RialticRoles.engWorkflows,
]

export enum Permissions {
  readConfig = 'read:config',
  writeConfig = 'write:config',
  readSystem = 'read:system',
  writeSystem = 'write:system',
  writeTeamContent = 'write:team-content',
  readTeamContent = 'read:team-content',
  readUsersAll = 'read:users-all',
  writeUsersAll = 'write:users-all',
  readWorkspacesAll = 'read:workspaces-all',
  writeWorkspacesAll = 'write:workspaces-all',
}

export enum WorkspacePermissions {
  readAnalytics = 'workspace:workspaceId#read:analytics',
  readPhi = 'workspace:workspaceId#read:phi',
  readConnectors = 'workspace:workspaceId#read:connector',
  writeConnectors = 'workspace:workspaceId#write:connector',
  readConnectorConfig = 'workspace:workspaceId#read:connector-config',
  writeConnectorConfig = 'workspace:workspaceId#write:connector-config',
  readSystem = 'workspace:workspaceId#read:system',
  writeSystem = 'workspace:workspaceId#write:system',
  readUsers = 'workspace:workspaceId#read:users',
  writeUsers = 'workspace:workspaceId#write:users',
  readWorkspace = 'workspace:workspaceId#read:workspace',
  writeWorkspace = 'workspace:workspaceId#write:workspace',
}

export type Rule = 'ANY' | 'ALL'

type RolesGuardOptions = { roles: string[]; userRoles?: string[]; rule: Rule }

const guard = (
  rule: Rule,
  endpointChecks: string[],
  userProps?: string[],
): boolean => {
  if (!userProps || !userProps.length) {
    return false
  }

  if (rule === 'ALL') {
    return endpointChecks.every((p) => userProps.includes(p))
  }

  return endpointChecks.some((p) => userProps.includes(p))
}

export const rolesGuard = ({ roles, rule, userRoles }: RolesGuardOptions) => {
  if (guard(rule, roles, userRoles)) {
    return true
  }

  throw new Error('missing role')
}

type PermissionsGuardOptions = {
  permissions: Permissions[]
  rule: 'ANY' | 'ALL'
  userPermissions?: string[]
  workspaceId?: string
}

export const permissionsGuard = ({
  permissions,
  userPermissions,
  rule,
}: PermissionsGuardOptions) => {
  if (!userPermissions) {
    throw new Error('no permissions')
  }

  if (guard(rule, permissions, userPermissions)) {
    return true
  }

  throw new Error('missing permission')
}

type WorkspacePermissionsGuardOptions = {
  permissions: WorkspacePermissions[]
  rialticPermissions: Permissions[]
  rule: 'ANY' | 'ALL'
  userPermissions?: string[]
  workspaceId?: string
}

export const workspacePermissionsGuard = ({
  workspaceId,
  userPermissions,
  permissions,
  rialticPermissions,
  rule,
}: WorkspacePermissionsGuardOptions) => {
  if (!workspaceId) {
    throw new Error('workspace id required')
  }

  if (rialticPermissions?.length) {
    const pass = permissionsGuard({
      rule,
      permissions: rialticPermissions,
      userPermissions,
    })

    if (pass) {
      return true
    }
  }

  const userWorkspacePermissions = userPermissions?.filter((p: string) =>
    p.startsWith('workspace:'),
  )

  if (!userWorkspacePermissions?.length) {
    throw new Error('no workspace permissions')
  }

  const endpointPermissions = permissions.map((p) => {
    const [, scope] = p.split('#')

    return `workspace:${workspaceId}#${scope}`
  })

  if (guard(rule, endpointPermissions, userWorkspacePermissions)) {
    return true
  }

  throw new Error('missing workspace permission')
}
